import { siteConfig } from '../config/site';

export interface PublicCounters {
  visitorsToday: number;
  visitorsTotal: number;
  formattingToday: number;
  formattingTotal: number;
}

const firstVisitKey = 'kopivaro-counter-first-visit-v1';
const visitDateKey = 'kopivaro-counter-visit-date-v1';

function localDate(date: Date): string {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = Object.fromEntries(formatter.formatToParts(date).map((part) => [part.type, part.value]));
  return `${parts.year}-${parts.month}-${parts.day}`;
}

function parseCounters(value: unknown): PublicCounters {
  if (!value || typeof value !== 'object') throw new Error('Ungültige Zählerantwort');
  const record = value as Record<string, unknown>;
  const keys: Array<keyof PublicCounters> = ['visitorsToday', 'visitorsTotal', 'formattingToday', 'formattingTotal'];
  const counters = {} as PublicCounters;
  for (const key of keys) {
    const number = record[key];
    if (typeof number !== 'number' || !Number.isSafeInteger(number) || number < 0) throw new Error('Ungültiger Zählerwert');
    counters[key] = number;
  }
  return counters;
}

async function requestCounters(body?: Record<string, unknown>): Promise<PublicCounters> {
  if (!siteConfig.publicCounters.enabled) throw new Error('Zähler deaktiviert');
  const response = await fetch(siteConfig.publicCounters.endpoint, {
    method: body ? 'POST' : 'GET',
    credentials: 'omit',
    cache: 'no-store',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!response.ok) throw new Error('Zähler nicht erreichbar');
  return parseCounters(await response.json());
}

export async function registerVisit(now = new Date()): Promise<PublicCounters> {
  const today = localDate(now);
  let newTotal: boolean;
  let newToday: boolean;
  try {
    newTotal = localStorage.getItem(firstVisitKey) !== '1';
    newToday = localStorage.getItem(visitDateKey) !== today;
  } catch {
    return requestCounters();
  }
  const counters = newTotal || newToday
    ? await requestCounters({ event: 'visit', newTotal, newToday })
    : await requestCounters();
  try {
    if (newTotal) localStorage.setItem(firstVisitKey, '1');
    if (newToday) localStorage.setItem(visitDateKey, today);
  } catch {
    // Die Zählung war erfolgreich; blockierter Browserspeicher beeinträchtigt das Werkzeug nicht.
  }
  return counters;
}

export function registerFormatting(): Promise<PublicCounters> {
  return requestCounters({ event: 'formatting' });
}
